/**
 * Last bank-statement import dossier for Fusion Case Files.
 * Lives outside fusion-ui prefs so a successful StatementFlow parse is available
 * on the Case files tab and can be promoted into a real LE case evidence row.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { SFAccountKyc, SFStatement } from '../services/statementFlow';
import type { TxDetail } from '../types/fusion';

export interface BankCounterpartyAgg {
  name: string;
  debit: number;
  credit: number;
  count: number;
}

export interface BankImportDossier {
  /** Crypto / LE case id when already bound (real case canvas). */
  leCaseId?: string;
  /** StatementFlow case id used for the upload. */
  sfCaseId: string;
  statementId: string;
  accountId: string;
  accountNodeId: string;
  fileName?: string;
  statement?: Pick<SFStatement, 'source_file_name' | 'narrative_summary' | 'risk_level' | 'anomalies' | 'extracted_txn_count'>;
  kyc: SFAccountKyc | null;
  summary: { txns: number; cps: number; dr: number; cr: number };
  transactions: TxDetail[];
  counterparties: BankCounterpartyAgg[];
  deepLink?: string;
  importedAt: string;
}

interface BankImportState {
  dossier: BankImportDossier | null;
  setDossier: (d: BankImportDossier | null) => void;
  clearDossier: () => void;
  bindLeCase: (leCaseId: string) => void;
}

export const useBankImportStore = create<BankImportState>()(
  persist(
    (set) => ({
      dossier: null,
      setDossier: (d) => set({ dossier: d }),
      clearDossier: () => set({ dossier: null }),
      bindLeCase: (leCaseId) => set((s) => (
        s.dossier ? { dossier: { ...s.dossier, leCaseId } } : {}
      )),
    }),
    {
      name: 'fusion-bank-import:dossier',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
